import { useContext, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { PageFrame } from "../../components/page_frame";
import { ROOT_ROLE } from "../../constants/user";
import { useCustomer, usePerspective } from "../../contexts/app/provider";
import { ApiContext } from "../../contexts/app/context";
import { AuthenticationService } from "../../services/perspective_api/authentication";
import {
  useMyHelpArticleQuery,
  useMyHelpArticlesQuery,
  usePrepareMyHelpArticleUploadMutation,
  usePublishMyHelpArticleMutation,
  useUnpublishMyHelpArticleMutation,
} from "../../services/perspective_api/__generated__/perspective_server-types";
import { HelpArticleView } from "./help_article_view";
import { HelpLibraryView } from "./help_library_view";
import { HelpPublisherView } from "./help_publisher_view";
import {
  publishHelpBundle,
  HelpPublicationCancelledError,
  uploadHelpFile,
  type HelpArticleMetadata,
} from "./publish_workflow";
import type { HelpArticle, HelpArticleSummary, HelpUpload } from "./types";

const hasPublisherRole = (
  actualRole: string | null,
  effectiveRole: string | null | undefined,
) => actualRole === ROOT_ROLE && effectiveRole === ROOT_ROLE;

const effectiveHelpCustomerId = (
  actualRole: string | null,
  selectedCustomerId: string | null,
  userCustomerId: string | null | undefined,
) => (actualRole === ROOT_ROLE ? selectedCustomerId : userCustomerId || null);

const articleSummary = (value: {
  slug: string;
  title: string;
  summary: string;
  category: string;
  updatedAt: unknown;
}): HelpArticleSummary => ({
  ...value,
  updatedAt: String(value.updatedAt),
});

const fullArticle = (value: {
  slug: string;
  title: string;
  summary: string;
  category: string;
  markdown: string;
  updatedAt: unknown;
  assets: Array<{ filename: string; mediaType: string; url: string }>;
}): HelpArticle => ({
  ...value,
  updatedAt: String(value.updatedAt),
  assets: value.assets.map(({ filename, mediaType, url }) => ({
    filename,
    mediaType,
    url,
  })),
});

export const HelpLibraryPage = () => {
  const navigate = useNavigate();
  const { perspectiveApiService } = usePerspective();
  const { whoIAm, selectedCustomerId } = useCustomer();
  const { actualRole } = useContext(ApiContext);
  const customerId = effectiveHelpCustomerId(
    actualRole,
    selectedCustomerId,
    whoIAm?.customerId,
  );
  const { data, loading, error, refetch } = useMyHelpArticlesQuery({
    client: perspectiveApiService.getClient(),
    context: { atomicCustomerId: customerId },
    skip: !customerId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  return (
    <PageFrame>
      <HelpLibraryView
        articles={(data?.myHelpArticles ?? []).map(articleSummary)}
        loading={loading}
        error={error ? "Help articles could not be loaded." : undefined}
        canPublish={hasPublisherRole(actualRole, whoIAm?.role)}
        customerId={customerId}
        onOpenArticle={(slug) => navigate(`/help/${slug}`)}
        onPublish={() => navigate("/help/publish")}
        onRetry={() => void refetch()}
      />
    </PageFrame>
  );
};

export const HelpArticlePage = () => {
  const navigate = useNavigate();
  const { slug = "" } = useParams<{ slug: string }>();
  const { perspectiveApiService } = usePerspective();
  const { whoIAm, selectedCustomerId } = useCustomer();
  const { actualRole } = useContext(ApiContext);
  const customerId = effectiveHelpCustomerId(
    actualRole,
    selectedCustomerId,
    whoIAm?.customerId,
  );
  const canDelete = actualRole === ROOT_ROLE && whoIAm?.role === ROOT_ROLE;
  const client = perspectiveApiService.getClient();
  const [unpublishArticle] = useUnpublishMyHelpArticleMutation({ client });
  const { data, loading, error, refetch } = useMyHelpArticleQuery({
    client,
    variables: { slug },
    context: { atomicCustomerId: customerId },
    skip: !slug || !customerId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  return (
    <PageFrame>
      <HelpArticleView
        key={`${customerId ?? "current-customer"}:${slug}`}
        article={data?.myHelpArticle ? fullArticle(data.myHelpArticle) : null}
        loading={loading}
        error={error ? "The article could not be loaded." : undefined}
        onBack={() => navigate("/help")}
        onRetry={() => void refetch()}
        onRefreshMedia={() => {
          void refetch().catch(() => undefined);
        }}
        canDelete={canDelete}
        onDelete={
          canDelete
            ? async () => {
                if (!customerId) {
                  throw new Error(
                    "Select a customer before deleting Help content.",
                  );
                }
                const result = await unpublishArticle({
                  variables: { slug },
                  context: { atomicCustomerId: customerId },
                });
                if (result.data?.unpublishMyHelpArticle === undefined) {
                  throw new Error("The article could not be deleted.");
                }
                navigate("/help");
              }
            : undefined
        }
      />
    </PageFrame>
  );
};

export const HelpPublisherPage = () => {
  const navigate = useNavigate();
  const { perspectiveApiService } = usePerspective();
  const { whoIAm, selectedCustomerId } = useCustomer();
  const { actualRole } = useContext(ApiContext);
  const customerId = effectiveHelpCustomerId(
    actualRole,
    selectedCustomerId,
    whoIAm?.customerId,
  );
  const activeCustomerId = useRef(customerId);
  activeCustomerId.current = customerId;
  const client = perspectiveApiService.getClient();
  const [prepareUpload] = usePrepareMyHelpArticleUploadMutation({ client });
  const [publishUpload] = usePublishMyHelpArticleMutation({ client });

  if (!hasPublisherRole(actualRole, whoIAm?.role)) {
    return <Navigate to="/help" replace />;
  }

  const handlePublish = async (
    metadata: HelpArticleMetadata,
    files: File[],
    onProgress: (completed: number, total: number) => void,
  ) => {
    const publishingCustomerId = customerId;
    if (!publishingCustomerId) {
      throw new Error("Select a customer before publishing Help content.");
    }
    const assertCustomerIsCurrent = () => {
      if (activeCustomerId.current !== publishingCustomerId) {
        throw new HelpPublicationCancelledError();
      }
    };
    const article = await publishHelpBundle({
      metadata,
      files,
      api: {
        prepare: async (input): Promise<HelpUpload> => {
          const result = await prepareUpload({
            variables: { input },
            context: { atomicCustomerId: publishingCustomerId },
          });
          const upload = result.data?.prepareMyHelpArticleUpload;
          if (!upload) throw new Error("The upload could not be prepared.");
          return {
            uploadId: upload.uploadId,
            expiresAt: String(upload.expiresAt),
            targets: upload.targets.map((target) => ({
              filename: target.filename,
              mediaType: target.mediaType,
              url: target.url,
              requiresAuth: target.requiresAuth,
              checksumSha256: target.checksumSha256,
            })),
          };
        },
        publish: async (uploadId) => {
          const result = await publishUpload({
            variables: { uploadId },
            context: { atomicCustomerId: publishingCustomerId },
          });
          const published = result.data?.publishMyHelpArticle.article;
          if (!published)
            throw new Error("The article could not be published.");
          return fullArticle(published);
        },
      },
      upload: (file, target) =>
        uploadHelpFile(file, target, {
          idToken: AuthenticationService.getIdToken() ?? "",
          selectedCustomerId: publishingCustomerId,
        }),
      onProgress,
      assertCustomerIsCurrent,
    });

    assertCustomerIsCurrent();
    navigate(`/help/${article.slug}`);
  };

  return (
    <PageFrame>
      <HelpPublisherView
        key={customerId ?? "current-customer"}
        onCancel={() => navigate("/help")}
        onPublish={handlePublish}
      />
    </PageFrame>
  );
};
